import { useAuthStore } from '@/lib/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

export class VoiceNoteUploadError extends Error {
  status?: number;
  url: string;
  details?: unknown;

  constructor(message: string, options: { status?: number; url: string; details?: unknown }) {
    super(message);
    this.name = 'VoiceNoteUploadError';
    this.status = options.status;
    this.url = options.url;
    this.details = options.details;
  }
}

export interface VoiceSmartUpdateResponse {
  success: boolean;
  updatedSummary: Record<string, unknown>;
  changes: Array<{
    field: string;
    action: 'update' | 'append' | 'replace';
    oldValue: unknown;
    newValue: unknown;
    timestamp: string;
  }>;
  transcribedText: string;
}

function buildVoiceTranscribeUrl(): string {
  if (!API_BASE_URL) {
    throw new VoiceNoteUploadError('Voice note service is unavailable right now', {
      url: '/api/voice/transcribe',
      details: 'Missing API base URL configuration',
    });
  }

  return `${API_BASE_URL}/api/voice/transcribe`;
}

function extractErrorMessage(details: unknown): string | null {
  if (typeof details === 'string' && details.trim()) {
    return details;
  }

  if (typeof details !== 'object' || details === null) {
    return null;
  }

  const data = details as Record<string, unknown>;
  const candidates = [data.error, data.message, data.detail];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return null;
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  // Backend failures are not guaranteed to be JSON. Handle plain-text and
  // other non-structured responses defensively so UI error reporting remains useful.
  return response.text();
}

function normalizeVoiceSmartUpdateResponse(payload: unknown): VoiceSmartUpdateResponse {
  const data = (payload ?? {}) as Record<string, unknown>;

  return {
    success: Boolean(data.success ?? true),
    updatedSummary:
      (data.updatedSummary as Record<string, unknown> | undefined) ||
      (data.updated_summary as Record<string, unknown> | undefined) ||
      (data.summary as Record<string, unknown> | undefined) ||
      {},
    changes: Array.isArray(data.changes)
      ? (data.changes as VoiceSmartUpdateResponse['changes'])
      : [],
    transcribedText:
      typeof data.transcribedText === 'string'
        ? data.transcribedText
        : typeof data.transcribed_text === 'string'
          ? data.transcribed_text
          : typeof data.transcription === 'string'
            ? data.transcription
            : '',
  };
}

/**
 * Transcribe audio and smart-update the consultation summary.
 * Sends the audio file + current summary JSON to the backend;
 * the AI transcribes, merges changes, and returns CDSS alerts.
 */
export async function transcribeAndUpdateSummary(
  audioBlob: Blob,
  currentSummary: Record<string, unknown>
): Promise<VoiceSmartUpdateResponse> {
  const token = useAuthStore.getState().token;
  if (!token) {
    throw new VoiceNoteUploadError('Voice note failed. Please log in again and retry.', {
      url: '/api/voice/transcribe',
      details: 'Missing authentication token',
    });
  }

  const url = buildVoiceTranscribeUrl();

  const formData = new FormData();
  // Backend contract: multipart field name must be `audio`.
  formData.append('audio', audioBlob, `voice-note-${Date.now()}.webm`);
  if (Object.keys(currentSummary).length > 0) {
    // Backend contract: optional `summary` is a stringified JSON snapshot used
    // for merge/update mode when transcription should patch existing case sheet data.
    formData.append('summary', JSON.stringify(currentSummary));
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  } catch (error) {
    throw new VoiceNoteUploadError('Voice note upload failed. Please try again.', {
      url,
      details: error,
    });
  }

  if (!response.ok) {
    const errorData = await parseResponsePayload(response).catch(() => null);
    const _backendMessage = extractErrorMessage(errorData);

    const friendlyMessage =
      response.status >= 500
        ? 'Voice note service is unavailable right now. Please try again later.'
        : 'Voice note upload failed. Please try again.';

    throw new VoiceNoteUploadError(friendlyMessage, {
      status: response.status,
      url,
      details: {
        backendMessage: _backendMessage,
        response: errorData,
      },
    });
  }

  const payload = await parseResponsePayload(response);
  return normalizeVoiceSmartUpdateResponse(payload);
}
